/**
 * SG Incorporation (Post) — 문서별 [[Signers]] 컨텍스트.
 *
 * 시드 규칙 (문서별 싸이너 가이드):
 *  · first_res     — 이사 전원 자동 시드 (DRIW)
 *  · share_ind_*   — 청약자(applicant) 자동 시드
 *  · share_corp_*  — 법인 대표(representative) 자동 시드
 *  · ddf_*         — 해당 이사(director_name) 자동 시드
 *  · 이사 1인 문서 — 자동 시드 없음 (수동 지정): form_a1, paid_up, nomination, indemnity
 *  · 양 당사자     — 자동 시드 없음 (수동): cosec, reg_addr, nominee
 *  · welcome / fy  — 서명 불요 (UI 없음)
 */
import { inject, provide, reactive, ref, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export type SgPostSignersCtx = {
  entityCd: Ref<string>;
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  seedFromValuesIfEmpty: (values: Record<string, any>) => void;
};

export const SG_POST_SIGNERS: InjectionKey<SgPostSignersCtx> = Symbol('sgIncPostSigners');

/** 단일 문서(패키지 공통) Signers varKey */
export const SG_POST_SIGNER_KEYS = {
  firstRes: 'first_res_Signers',
  formA1: 'form_a1_Signers',
  paidUp: 'paid_up_Signers',
  cosecAgr: 'cosec_agr_Signers',
  regAddr: 'reg_addr_Signers',
  nominee: 'nominee_Signers',
  nomination: 'nomination_Signers',
  indemnity: 'indemnity_Signers',
} as const;

/** 이사 1인 — 자동 시드하지 않음 (수동 지정) */
export const SG_POST_MANUAL_ONE_DIRECTOR_KEYS: string[] = [
  SG_POST_SIGNER_KEYS.formA1,
  SG_POST_SIGNER_KEYS.paidUp,
  SG_POST_SIGNER_KEYS.nomination,
  SG_POST_SIGNER_KEYS.indemnity,
];

/** 회사&CSP / 양 당사자 — 자동 시드하지 않음 (수동 지정) */
export const SG_POST_MANUAL_PARTIES_KEYS: string[] = [
  SG_POST_SIGNER_KEYS.cosecAgr,
  SG_POST_SIGNER_KEYS.regAddr,
  SG_POST_SIGNER_KEYS.nominee,
];

export function shareIndSignersKey(id: number | string) {
  return `share_ind_${id}_Signers`;
}
export function shareCorpSignersKey(id: number | string) {
  return `share_corp_${id}_Signers`;
}
export function ddfSignersKey(id: number | string) {
  return `ddf_${id}_Signers`;
}

function entry(
  id: string,
  name: string,
  title: string,
  role = 'director',
): SignerEntry {
  return {
    id,
    name,
    title: title || '',
    role,
    isManual: false,
    showTitle: !!title,
    _signerLayout: 'box',
  };
}

/** First Directors Resolution — 이사 전원 */
export function buildAllDirectorsSigners(values: Record<string, any>): SignerEntry[] {
  const out: SignerEntry[] = [];
  const src = values && typeof values === 'object' ? values : {};
  const seen = new Set<string>();

  for (let i = 1; i <= 30; i++) {
    const name = String(src[`dir_${i}/d_name`] || src[`dir${i}_director_name`] || '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push(entry(`sg-post-dir-${i}`, name, 'Director', 'director'));
  }
  return out;
}

/** DocScope 접두사에서 id 추출 (share_ind_2/ → 2) */
function scopedIds(values: Record<string, any>, prefix: string): number[] {
  const ids = new Set<number>();
  for (const k of Object.keys(values || {})) {
    const m = k.match(new RegExp(`^${prefix}(\\d+)/`));
    if (m) ids.add(Number(m[1]));
  }
  return [...ids].sort((a, b) => a - b);
}

function pickScoped(values: Record<string, any>, scope: string, bare: string): string {
  return String(values[`${scope}${bare}`] || values[bare] || '').trim();
}

/**
 * 문서별 시드 맵 생성 (비어 있는 키만 채울 때 사용).
 * 수동 지정 문서는 맵에 넣지 않음.
 */
export function buildSgPostAutoSignerSeeds(
  values: Record<string, any>,
): Record<string, SignerEntry[]> {
  const out: Record<string, SignerEntry[]> = {};
  const src = values && typeof values === 'object' ? values : {};

  // 1) First Res — 이사 전원
  const allDirs = buildAllDirectorsSigners(src);
  if (allDirs.length) {
    out[SG_POST_SIGNER_KEYS.firstRes] = allDirs.map((s) => ({
      ...s,
      id: `${SG_POST_SIGNER_KEYS.firstRes}-${s.id}`,
    }));
  }

  // 2) Share Application Individual — 청약자 본인
  for (const id of scopedIds(src, 'share_ind_')) {
    const scope = `share_ind_${id}/`;
    const name = pickScoped(src, scope, 'applicant_name');
    if (!name) continue;
    const key = shareIndSignersKey(id);
    out[key] = [entry(`${key}-1`, name, 'Applicant / Subscriber', 'shareholder')];
  }

  // 3) Share Application Corporate — 법인 대표
  for (const id of scopedIds(src, 'share_corp_')) {
    const scope = `share_corp_${id}/`;
    const name = pickScoped(src, scope, 'representative_name');
    const title = pickScoped(src, scope, 'representative_title') || 'Authorised Representative';
    if (!name) continue;
    const key = shareCorpSignersKey(id);
    out[key] = [entry(`${key}-1`, name, title, 'shareholder')];
  }

  // 4) DDF — 해당 이사 본인
  for (const id of scopedIds(src, 'ddf_')) {
    const scope = `ddf_${id}/`;
    const name = pickScoped(src, scope, 'director_name');
    if (!name) continue;
    const key = ddfSignersKey(id);
    out[key] = [entry(`${key}-1`, name, 'Director', 'director')];
  }

  // 이사 1인 / 양 당사자 문서는 시드하지 않음 (수동)
  return out;
}

/** @deprecated createTask fallback — First Res 용만 */
export function buildSgPostSignersFromValues(values: Record<string, any>): SignerEntry[] {
  return buildAllDirectorsSigners(values);
}

export function provideSgPostSigners(entityCdRef?: Ref<string>): SgPostSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  const signerEntityCd = (entityCdRef as Ref<string>) || ref('');

  const ctx: SgPostSignersCtx = {
    entityCd: signerEntityCd,
    signersOf: (varKey: string) => store[varKey] || [],
    setSigners: (varKey: string, list: SignerEntry[]) => {
      store[varKey] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) {
        if (v?.length) out[k] = v.map((s) => ({ ...s }));
      }
      return out;
    },
    importAll: (map) => {
      for (const k of Object.keys(store)) delete store[k];
      if (!map || typeof map !== 'object') return;
      for (const [k, v] of Object.entries(map)) {
        if (Array.isArray(v)) store[k] = v.map((s) => ({ ...s }));
      }
    },
    seedFromValuesIfEmpty: (values) => {
      const seeds = buildSgPostAutoSignerSeeds(values);
      for (const [key, list] of Object.entries(seeds)) {
        if (!store[key]?.length && list.length) {
          store[key] = list.map((s) => ({ ...s }));
        }
      }
    },
  };

  provide(SG_POST_SIGNERS, ctx);
  return ctx;
}

export function useSgPostSigners(): SgPostSignersCtx {
  const ctx = inject(SG_POST_SIGNERS, null);
  if (!ctx) {
    const emptyCd = ref('');
    return {
      entityCd: emptyCd,
      signersOf: () => [],
      setSigners: () => {},
      exportAll: () => ({}),
      importAll: () => {},
      seedFromValuesIfEmpty: () => {},
    };
  }
  return ctx;
}
